import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Navigation, MapPin, Clock, Route as RouteIcon, Wand2, Loader2, ArrowUp, ArrowDown, Save, Undo2 } from 'lucide-react';
import { apiWorkorders, apiCustomers, team, geo } from '../../lib/api';
import { toast } from 'sonner';
import { today, normDate } from '../../lib/dates';
import { optimizeOrder, routeMiles, arrivalTimes, clockLabel } from '../../lib/routing';

const parseHour = (t) => { const m = String(t || '').match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/i); if (!m) return 99; let h = parseInt(m[1], 10); const ap = (m[3] || '').toUpperCase(); if (ap === 'PM' && h < 12) h += 12; if (ap === 'AM' && h === 12) h = 0; return h + (parseInt(m[2] || '0', 10) / 60); };
const statusColor = { Completed: 'bg-emerald-100 text-emerald-700', 'In Progress': 'bg-blue-100 text-blue-700', 'En Route': 'bg-amber-100 text-amber-700', Scheduled: 'bg-slate-100 text-slate-700', Cancelled: 'bg-red-100 text-red-700' };
// Fields this page adds for display only; never written back to the job.
const strip = ({ _addr, ...job }) => job;

export default function RoutesPage() {
  const [date, setDate] = useState(today());
  const [jobs, setJobs] = useState([]);
  const [techs, setTechs] = useState([]);
  const [coords, setCoords] = useState({});
  const [loading, setLoading] = useState(true);
  // Unsaved order per technician: { [techKey]: [jobId, ...] }
  const [drafts, setDrafts] = useState({});
  const [updateTimes, setUpdateTimes] = useState(false);
  const [startAt, setStartAt] = useState('08:00');
  const [serviceMin, setServiceMin] = useState(30);
  const [saving, setSaving] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wo, cust, tk] = await Promise.all([apiWorkorders.list().catch(() => []), apiCustomers.list().catch(() => []), team.technicians().catch(() => [])]);
      const cm = Object.fromEntries((cust || []).map((c) => [c.id, c]));
      const dayJobs = (wo || []).filter((j) => normDate(j.date) === date && j.status !== 'Cancelled').map((j) => {
        const c = cm[j.customerId];
        return { ...j, _addr: c ? [j.address || c.address, c.city, c.state, c.zip].filter(Boolean).join(', ') : (j.address || '') };
      });
      setJobs(dayJobs);
      setTechs(tk || []);
      setDrafts({});
      const addrs = [...new Set(dayJobs.map((j) => j._addr).filter(Boolean))];
      if (addrs.length) {
        // The geocoder takes 25 at a time; results are cached on the server.
        const found = {};
        for (let i = 0; i < addrs.length; i += 25) Object.assign(found, await geo.batch(addrs.slice(i, i + 25)).catch(() => ({})));
        setCoords(found);
      }
    } finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const coordOf = useCallback((s) => coords[s._addr], [coords]);
  const techKey = (t) => t.email || t.name;
  const startMinutes = useMemo(() => { const [h, m] = startAt.split(':').map(Number); return (h || 8) * 60 + (m || 0); }, [startAt]);

  /** Saved route order for the day first, then appointment time. */
  const savedOrder = (stops) => [...stops].sort((a, b) => {
    const ra = a.routeDate === date && a.routeOrder ? a.routeOrder : 999;
    const rb = b.routeDate === date && b.routeOrder ? b.routeOrder : 999;
    return ra - rb || parseHour(a.time) - parseHour(b.time);
  });

  const routeFor = (tech) => {
    const mine = jobs.filter((j) => (tech.email && j.techEmail === tech.email) || j.tech === tech.name || (tech.techId && j.techId === tech.techId));
    const draft = drafts[techKey(tech)];
    const stops = draft ? draft.map((id) => mine.find((j) => j.id === id)).filter(Boolean) : savedOrder(mine);
    const etas = arrivalTimes(stops, coordOf, { startMinutes, serviceMinutes: Number(serviceMin) || 30 });
    return { stops, etas, miles: routeMiles(stops.map(coordOf)), savedMiles: routeMiles(savedOrder(mine).map(coordOf)), dirty: !!draft };
  };

  const optimize = (tech) => {
    const { stops } = routeFor(tech);
    const better = optimizeOrder(stops, coordOf);
    setDrafts((d) => ({ ...d, [techKey(tech)]: better.map((s) => s.id) }));
    const missing = stops.filter((s) => coordOf(s)?.lat == null).length;
    if (missing) toast.message(`${missing} stop(s) have no map location and stay at the end`);
  };

  const move = (tech, index, dir) => {
    const { stops } = routeFor(tech);
    const ids = stops.map((s) => s.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    setDrafts((d) => ({ ...d, [techKey(tech)]: ids }));
  };

  const save = async (tech) => {
    const { stops, etas } = routeFor(tech);
    setSaving(techKey(tech));
    try {
      for (let i = 0; i < stops.length; i += 1) {
        const s = stops[i];
        const patch = { routeOrder: i + 1, routeDate: date, routeEta: clockLabel(etas[i]) };
        if (updateTimes) patch.time = clockLabel(etas[i]);
        await apiWorkorders.update(s.id, { ...strip(s), ...patch });
      }
      toast.success(`${tech.name}'s route saved — the tech app shows stops in this order`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the route');
    } finally {
      setSaving('');
    }
  };

  const openInMaps = (stops) => {
    const addrs = stops.map((s) => s._addr).filter(Boolean);
    if (!addrs.length) { toast.error('No addresses to navigate'); return; }
    window.open(`https://www.google.com/maps/dir/${addrs.map((a) => encodeURIComponent(a)).join('/')}`, '_blank');
  };

  const unassigned = jobs.filter((j) => !techs.some((t) => (t.email && j.techEmail === t.email) || j.tech === t.name || (t.techId && j.techId === t.techId)));

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <div className="flex flex-wrap justify-between gap-3 items-end">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><RouteIcon className="w-7 h-7 text-[#8BC53F]" />Routes & Dispatch</h1>
          <p className="text-slate-500 mt-1">{jobs.length} stops across {techs.length} technician(s). Optimize, adjust, then save — techs see stops in the saved order.</p>
        </div>
        <div className="flex gap-3 items-end flex-wrap">
          <div><div className="text-xs text-slate-500 mb-1">Day</div><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 w-44" data-testid="routes-date-input" /></div>
          <div><div className="text-xs text-slate-500 mb-1">Leave office</div><Input type="time" value={startAt} onChange={(e) => setStartAt(e.target.value)} className="h-10 w-32" /></div>
          <div><div className="text-xs text-slate-500 mb-1">Minutes per stop</div><Input type="number" min="5" step="5" value={serviceMin} onChange={(e) => setServiceMin(e.target.value)} className="h-10 w-24" /></div>
          <label className="flex items-center gap-2 text-sm text-slate-700 h-10"><input type="checkbox" checked={updateTimes} onChange={(e) => setUpdateTimes(e.target.checked)} className="w-4 h-4 accent-[#8BC53F]" />Also set appointment times to the ETAs</label>
        </div>
      </div>

      {loading ? <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
        <div className="grid lg:grid-cols-2 gap-5">
          {techs.map((t, ti) => {
            const { stops, etas, miles, savedMiles, dirty } = routeFor(t);
            const key = techKey(t);
            return (
              <Card key={key} className="border-slate-200 overflow-hidden" data-testid={`route-${t.email}`}>
                <div className="p-4 flex items-center justify-between gap-2 flex-wrap" style={{ background: ['#0d2818', '#16341f'][ti % 2] }}>
                  <div className="flex items-center gap-3 text-white">
                    <div className="w-10 h-10 rounded-full text-white text-sm font-bold flex items-center justify-center" style={{ background: ['#8BC53F', '#0ea5e9', '#a855f7', '#f97316'][ti % 4] }}>{(t.name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('')}</div>
                    <div>
                      <div className="font-bold">{t.name}</div>
                      <div className="text-xs text-white/70">{stops.length} stops · ~{miles} mi{dirty && savedMiles > miles ? ` (saves ${Math.round((savedMiles - miles) * 10) / 10} mi)` : ''}</div>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="secondary" className="gap-1 h-8" onClick={() => optimize(t)} disabled={stops.length < 2} data-testid={`optimize-${t.email}`}><Wand2 className="w-3.5 h-3.5" />Optimize</Button>
                    {dirty && <Button size="sm" variant="secondary" className="gap-1 h-8" onClick={() => setDrafts((d) => { const n = { ...d }; delete n[key]; return n; })}><Undo2 className="w-3.5 h-3.5" />Undo</Button>}
                    <Button size="sm" className="gap-1 h-8 bg-[#8BC53F] hover:bg-[#6FA82C]" onClick={() => save(t)} disabled={!stops.length || saving === key} data-testid={`save-route-${t.email}`}><Save className="w-3.5 h-3.5" />{saving === key ? 'Saving…' : 'Save route'}</Button>
                    <Button size="sm" variant="secondary" className="gap-1 h-8" onClick={() => openInMaps(stops)} disabled={!stops.length}><Navigation className="w-3.5 h-3.5" />Maps</Button>
                  </div>
                </div>
                {dirty && <div className="px-4 py-1.5 bg-amber-50 text-amber-800 text-xs">Not saved yet — the tech still sees the previous order.</div>}
                <div className="divide-y divide-slate-100">
                  {stops.length === 0 ? <div className="p-6 text-center text-sm text-slate-400">No stops assigned.</div> :
                    stops.map((s, i) => (
                      <div key={s.id} className="p-3 flex items-center gap-3" data-testid={`stop-${s.id}`}>
                        <div className="w-7 h-7 rounded-full bg-[#8BC53F] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900 text-sm truncate">{s.customer}</div>
                          <div className="text-xs text-slate-500 truncate flex items-center gap-1"><MapPin className="w-3 h-3" />{s._addr || 'No address'}{coordOf(s)?.lat == null ? ' · not on map' : ''}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-xs font-semibold text-slate-700 flex items-center gap-1 justify-end"><Clock className="w-3 h-3" />{s.time || '—'}</div>
                          <div className="text-[10px] text-slate-500">ETA {clockLabel(etas[i])}</div>
                          <Badge className={`${statusColor[s.status] || ''} border-0 text-[9px] font-medium mt-0.5`}>{s.status}</Badge>
                        </div>
                        <div className="flex flex-col">
                          <button onClick={() => move(t, i, -1)} disabled={i === 0} className="p-0.5 text-slate-400 hover:text-slate-800 disabled:opacity-30" aria-label="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
                          <button onClick={() => move(t, i, 1)} disabled={i === stops.length - 1} className="p-0.5 text-slate-400 hover:text-slate-800 disabled:opacity-30" aria-label="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))}
                </div>
              </Card>
            );
          })}

          {unassigned.length > 0 && (
            <Card className="border-amber-200 bg-amber-50/40 overflow-hidden" data-testid="unassigned-route">
              <div className="p-4 bg-amber-100 font-bold text-amber-800">Unassigned · {unassigned.length} stops</div>
              <div className="divide-y divide-amber-100">
                {unassigned.map((s) => (
                  <div key={s.id} className="p-3 flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-amber-400 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">!</div>
                    <div className="flex-1 min-w-0"><div className="font-semibold text-slate-900 text-sm truncate">{s.customer}</div><div className="text-xs text-slate-500 truncate">{s._addr}</div></div>
                    <div className="text-xs font-semibold text-slate-700">{s.time}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {techs.length === 0 && <Card className="p-10 border-slate-200 text-center text-slate-500 lg:col-span-2">No technician accounts yet.</Card>}
        </div>
      )}
    </div>
  );
}
